import { useEffect, useState } from "react";
import { NavLink, Route, Routes, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Header } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { LayoutDashboard, User, Briefcase, Gift, MessageSquare, BarChart3, CreditCard, Settings, TrendingUp, Users, Calendar, Star, KeyRound } from "lucide-react";
import { ProProfileEditor, AdminProsApproval } from "@/pages/ProAdmin";
import { CommitmentTerm, AdminSearchReport } from "@/pages/Onda1";
import { ProSlotsManager, AdminReviews } from "@/pages/Onda2";
import { AdminWhatsapp } from "@/pages/AdminWhatsapp";
import { ProBenefitsManager } from "@/pages/ProBenefits";
import { AdminPasswords } from "@/pages/AdminPasswords";

function DashShell({ children, menu, title }) {
  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header/>
      <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 grid md:grid-cols-[220px,1fr] gap-6 flex-1">
        <aside className="hidden md:block">
          <nav className="rounded-2xl bg-white border p-2 gs-shadow sticky top-20">
            {menu.map(m=>(
              <NavLink key={m.to} to={m.to} end data-testid={m.testid} className={({isActive})=>`flex items-center gap-2 px-3 py-2 rounded-xl text-sm ${isActive?"bg-[#E6F4F1] text-[#0D7C66] font-semibold":"text-slate-700 hover:bg-slate-50"}`}>
                <m.icon className="w-4 h-4"/>{m.label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <div>
          {title && <h1 className="text-2xl font-extrabold mb-5" style={{fontFamily:"Plus Jakarta Sans"}}>{title}</h1>}
          {children}
        </div>
      </div>
    </div>
  );
}

const PRO_MENU = [
  {to:"/painel/profissional", icon:LayoutDashboard, label:"Dashboard", testid:"pro-menu-dashboard"},
  {to:"/painel/profissional/perfil", icon:User, label:"Meu Perfil", testid:"pro-menu-perfil"},
  {to:"/painel/profissional/servicos", icon:Briefcase, label:"Serviços", testid:"pro-menu-servicos"},
  {to:"/painel/profissional/beneficios", icon:Gift, label:"Benefícios", testid:"pro-menu-beneficios"},
  {to:"/painel/profissional/leads", icon:MessageSquare, label:"Leads", testid:"pro-menu-leads"},
  {to:"/painel/profissional/agenda", icon:Calendar, label:"Agenda", testid:"pro-menu-agenda"},
  {to:"/painel/profissional/relatorios", icon:BarChart3, label:"Relatórios", testid:"pro-menu-relatorios"},
  {to:"/painel/profissional/plano", icon:CreditCard, label:"Meu Plano", testid:"pro-menu-plano"},
  {to:"/painel/profissional/termo", icon:BarChart3, label:"Termo", testid:"pro-menu-termo"},
];

function KPI({label, value, hint, icon:Icon, color="text-[#0D7C66]"}) {
  return (
    <Card className="p-5 border-slate-200 gs-shadow">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        {Icon && <Icon className={`w-4 h-4 ${color}`}/>}
      </div>
      <div className="text-3xl font-extrabold mt-2" style={{fontFamily:"Plus Jakarta Sans"}}>{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </Card>
  );
}

function ProDashboard() {
  const [leads, setLeads] = useState([]);
  useEffect(()=>{ api.get("/leads").then(r=>setLeads(r.data)); }, []);
  const s = (st) => leads.filter(l=>l.status===st).length;
  return (
    <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Leads totais" value={leads.length} icon={MessageSquare}/>
        <KPI label="Novos" value={s("NEW")} icon={TrendingUp} color="text-[#FF7D54]"/>
        <KPI label="Contatados" value={s("CONTACTED")} icon={Users}/>
        <KPI label="Convertidos" value={s("CONVERTED")}/>
      </div>
      <Card className="mt-6 p-5 border-slate-200 gs-shadow">
        <div className="font-bold mb-4">Últimos leads</div>
        <div className="space-y-2">
          {leads.slice(0,8).map(l=>(
            <div key={l.id} className="flex justify-between items-center border-b border-slate-100 py-2">
              <div><div className="font-semibold text-sm">{l.name}</div><div className="text-xs text-slate-500">{l.service||"—"} • {l.city||""}</div></div>
              <Badge className="bg-[#E6F4F1] text-[#0D7C66] border-0">{l.status}</Badge>
            </div>
          ))}
          {leads.length===0 && <div className="text-sm text-slate-500">Nenhum lead ainda. Quando um associado clicar em "Tenho Interesse" no seu perfil, aparecerá aqui.</div>}
        </div>
      </Card>
    </>
  );
}

function ProLeads() {
  const [leads, setLeads] = useState([]);
  useEffect(()=>{ api.get("/leads").then(r=>setLeads(r.data)); }, []);
  const updateStatus = async (id, status) => {
    const { data } = await api.patch(`/leads/${id}/status`, { status });
    setLeads(prev=>prev.map(l=>l.id===id?data:l));
  };
  const statuses = ["NEW","RECEIVED","CONTACTED","SCHEDULED","ATTENDED","CONVERTED","NOT_CONVERTED","CANCELLED","CLOSED"];
  return (
    <Card className="p-5 border-slate-200 gs-shadow">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-widest text-slate-500"><tr><th className="text-left py-2">Nº</th><th className="text-left">Nome</th><th className="text-left">Contato</th><th className="text-left">Serviço</th><th className="text-left">Status</th></tr></thead>
          <tbody>
            {leads.map(l=>(
              <tr key={l.id} className="border-t border-slate-100" data-testid={`prolead-${l.number}`}>
                <td className="py-3 font-mono text-xs">{l.number}</td>
                <td>{l.name}</td>
                <td className="text-xs">{l.phone}<br/>{l.email}</td>
                <td>{l.service||"—"}</td>
                <td><select data-testid={`prolead-status-${l.number}`} value={l.status} onChange={e=>updateStatus(l.id, e.target.value)} className="h-8 rounded-md border px-2 text-xs">{statuses.map(s=><option key={s}>{s}</option>)}</select></td>
              </tr>
            ))}
          </tbody>
        </table>
        {leads.length===0 && <div className="text-slate-500 text-sm py-6 text-center">Nenhum lead ainda.</div>}
      </div>
    </Card>
  );
}

export function ProfissionalPainel() {
  const { user, ready } = useAuth();
  if (!ready) return <div className="p-10">Carregando…</div>;
  if (!user) return <Navigate to="/login"/>;
  return (
    <DashShell menu={PRO_MENU} title="Painel do Profissional">
      <Routes>
        <Route index element={<ProDashboard/>}/>
        <Route path="perfil" element={<ProProfileEditor/>}/>
        <Route path="servicos" element={<Card className="p-6 gs-shadow">Cadastro de serviços oferecidos.</Card>}/>
        <Route path="beneficios" element={<ProBenefitsManager/>}/>
        <Route path="leads" element={<ProLeads/>}/>
        <Route path="relatorios" element={<Card className="p-6 gs-shadow">Relatórios de leads, visualizações e conversões (em breve).</Card>}/>
        <Route path="plano" element={<Card className="p-6 gs-shadow">Você está no plano <b>Presença</b>. <div className="mt-2 text-xs text-slate-500">Pagamentos mockados no MVP.</div></Card>}/>
        <Route path="termo" element={<CommitmentTerm/>}/>
      </Routes>
    </DashShell>
  );
}

const CLINIC_MENU = [
  {to:"/painel/clinica", icon:LayoutDashboard, label:"Dashboard", testid:"cli-menu-dashboard"},
  {to:"/painel/clinica/equipe", icon:Users, label:"Equipe", testid:"cli-menu-equipe"},
  {to:"/painel/clinica/servicos", icon:Briefcase, label:"Serviços", testid:"cli-menu-servicos"},
  {to:"/painel/clinica/beneficios", icon:Gift, label:"Benefícios", testid:"cli-menu-beneficios"},
  {to:"/painel/clinica/leads", icon:MessageSquare, label:"Leads", testid:"cli-menu-leads"},
];

export function ClinicaPainel() {
  const { user, ready } = useAuth();
  if (!ready) return <div className="p-10">Carregando…</div>;
  if (!user) return <Navigate to="/login"/>;
  return (
    <DashShell menu={CLINIC_MENU} title="Painel da Clínica">
      <Routes>
        <Route index element={<div className="grid sm:grid-cols-3 gap-4">
          <KPI label="Profissionais" value={0}/><KPI label="Leads" value={0}/><KPI label="Benefícios" value={0}/></div>}/>
        <Route path="equipe" element={<Card className="p-6 gs-shadow">Vincule profissionais à sua clínica.</Card>}/>
        <Route path="servicos" element={<Card className="p-6 gs-shadow">Gerencie os serviços da clínica.</Card>}/>
        <Route path="beneficios" element={<ProBenefitsManager/>}/>
        <Route path="leads" element={<ProLeads/>}/>
      </Routes>
    </DashShell>
  );
}

const ADMIN_MENU = [
  {to:"/admin", icon:LayoutDashboard, label:"Dashboard", testid:"adm-menu-dashboard"},
  {to:"/admin/associados", icon:Users, label:"Associados", testid:"adm-menu-associados"},
  {to:"/admin/profissionais", icon:User, label:"Profissionais", testid:"adm-menu-profissionais"},
  {to:"/admin/clinicas", icon:Briefcase, label:"Clínicas", testid:"adm-menu-clinicas"},
  {to:"/admin/leads", icon:MessageSquare, label:"Leads", testid:"adm-menu-leads"},
  {to:"/admin/avaliacoes", icon:Star, label:"Avaliações", testid:"adm-menu-avaliacoes"},
  {to:"/admin/whatsapp", icon:MessageSquare, label:"WhatsApp Bot", testid:"adm-menu-whatsapp"},
  {to:"/admin/senhas", icon:KeyRound, label:"Senhas", testid:"adm-menu-senhas"},
  {to:"/admin/beneficios", icon:Gift, label:"Benefícios", testid:"adm-menu-beneficios"},
  {to:"/admin/planos", icon:CreditCard, label:"Planos", testid:"adm-menu-planos"},
  {to:"/admin/relatorios", icon:BarChart3, label:"Relatórios", testid:"adm-menu-relatorios"},
  {to:"/admin/config", icon:Settings, label:"Configurações", testid:"adm-menu-config"},
];

function AdminDashboard() {
  const [m, setM] = useState(null);
  useEffect(()=>{ api.get("/admin/metrics").then(r=>setM(r.data)).catch(()=>{}); }, []);
  if (!m) return <div>Carregando…</div>;
  const formatR = (v) => `R$ ${v.toFixed(2).replace(".", ",")}`;
  return (
    <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Associados ativos" value={m.associates_active} icon={Users}/>
        <KPI label="Profissionais" value={m.professionals} icon={User}/>
        <KPI label="Clínicas" value={m.clinics} icon={Briefcase}/>
        <KPI label="Benefícios ativos" value={m.benefits_active} icon={Gift}/>
        <KPI label="Leads totais" value={m.leads_total} icon={MessageSquare}/>
        <KPI label="Leads novos" value={m.leads_new} color="text-[#FF7D54]"/>
        <KPI label="Convertidos" value={m.leads_converted} icon={TrendingUp}/>
        <KPI label="MRR estimado" value={formatR(m.mrr)} icon={CreditCard}/>
        <KPI label="Inadimplência" value={`${m.delinquency_pct}%`}/>
        <KPI label="Cancelamentos" value={`${m.cancellation_pct}%`}/>
      </div>
      <Card className="mt-6 p-5 gs-shadow">
        <div className="font-bold mb-2">Visão executiva</div>
        <p className="text-sm text-slate-600">Métricas em tempo real da operação regional na Baixada Santista. Dados de pagamento e receita estão em modo demonstrativo no MVP.</p>
      </Card>
    </>
  );
}

function AdminUsers({ role, testid }) {
  const [items, setItems] = useState([]);
  useEffect(()=>{ api.get("/admin/users",{params:{role}}).then(r=>setItems(r.data)); }, [role]);
  return (
    <Card className="p-5 gs-shadow" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-widest text-slate-500"><tr><th className="text-left py-2">Nome</th><th className="text-left">E-mail</th><th className="text-left">Cidade</th><th className="text-left">Status</th></tr></thead>
        <tbody>
          {items.map(u=>(
            <tr key={u.id} className="border-t border-slate-100"><td className="py-3">{u.name}</td><td>{u.email}</td><td>{u.city||"—"}</td><td><Badge className="bg-[#E6F4F1] text-[#0D7C66] border-0">{u.status}</Badge></td></tr>
          ))}
        </tbody>
      </table>
      {items.length===0 && <div className="text-slate-500 text-sm py-6 text-center">Nenhum registro.</div>}
    </Card>
  );
}

export function AdminPainel() {
  const { user, ready } = useAuth();
  if (!ready) return <div className="p-10">Carregando…</div>;
  if (!user || !["admin","master"].includes(user.role)) return <Navigate to="/login"/>;
  return (
    <DashShell menu={ADMIN_MENU} title="Painel Administrativo">
      <Routes>
        <Route index element={<AdminDashboard/>}/>
        <Route path="associados" element={<AdminUsers role="associate" testid="admin-associados"/>}/>
        <Route path="profissionais" element={<AdminProsApproval/>}/>
        <Route path="clinicas" element={<AdminUsers role="clinic" testid="admin-clinicas"/>}/>
        <Route path="leads" element={<AdminLeads/>}/>
        <Route path="avaliacoes" element={<AdminReviews/>}/>
        <Route path="whatsapp" element={<AdminWhatsapp/>}/>
        <Route path="senhas" element={<AdminPasswords/>}/>
        <Route path="beneficios" element={<Card className="p-6 gs-shadow">Gestão de benefícios.</Card>}/>
        <Route path="planos" element={<AdminPlans/>}/>
        <Route path="relatorios" element={<AdminSearchReport/>}/>
        <Route path="config" element={<Card className="p-6 gs-shadow">Configurações da plataforma.</Card>}/>
      </Routes>
    </DashShell>
  );
}

function AdminLeads() {
  const [items, setItems] = useState([]);
  useEffect(()=>{ api.get("/leads").then(r=>setItems(r.data)); }, []);
  return (
    <Card className="p-5 gs-shadow">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-widest text-slate-500"><tr><th className="text-left py-2">Nº</th><th className="text-left">Nome</th><th className="text-left">Cidade</th><th className="text-left">Status</th><th className="text-left">Criado</th></tr></thead>
        <tbody>
          {items.map(l=>(<tr key={l.id} className="border-t border-slate-100"><td className="py-3 font-mono text-xs">{l.number}</td><td>{l.name}</td><td>{l.city||"—"}</td><td><Badge className="bg-[#E6F4F1] text-[#0D7C66] border-0">{l.status}</Badge></td><td className="text-xs text-slate-500">{new Date(l.created_at).toLocaleString("pt-BR")}</td></tr>))}
        </tbody>
      </table>
      {items.length===0 && <div className="text-slate-500 text-sm py-6 text-center">Nenhum lead ainda.</div>}
    </Card>
  );
}

function AdminPlans() {
  const [plans, setPlans] = useState([]);
  useEffect(()=>{ api.get("/plans").then(r=>setPlans(r.data)); }, []);
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {plans.map(p=>(
        <Card key={p.id} className="p-5 gs-shadow" data-testid={`admin-plan-${p.id}`}>
          <div className="flex items-center justify-between"><Badge className="bg-[#E6F4F1] text-[#0D7C66] border-0">{p.audience}</Badge><div className="text-lg font-extrabold">R$ {p.price.toFixed(2).replace(".",",")}</div></div>
          <div className="text-lg font-semibold mt-1">{p.name}</div>
          <ul className="mt-3 text-sm space-y-1 text-slate-600">{p.features.map(f=><li key={f}>• {f}</li>)}</ul>
        </Card>
      ))}
    </div>
  );
}
